export interface ReleaseNote {
	id: string
	version: string
	date: string
	title: string
	summary: string
	items: readonly string[]
}

export const RELEASE_NOTES: readonly ReleaseNote[] = [
	{
		id: '2.2-collection-sync-refresh',
		version: '2.2',
		date: 'October 30, 2026',
		title: 'Fixes and sync',
		summary: 'Reliability and security fixes & pull-to-refresh sync.',
		items: [
			'Improved reliability of collection sync and imports.',
			'Fixed issues with listen dates, filters, and switching accounts.',
			'Security improvements.',
			'Pull to refresh syncs your Discogs collection.',
		],
	},
	{
		id: '2.1-collection-layouts-release-data',
		version: '2.1',
		date: 'October 28, 2026',
		title: 'Collection layouts and release data',
		summary:
			'Browse your collection in new layouts, move all your release data in one file, and enjoy a faster, more polished app.',
		items: [
			'Added compact and cover-only collection layouts.',
			'Added a combined release data import and export for listening history, favorites, and hidden status.',
			'Filters can now be searched.',
			'Support messages can send you a copy, and bug reports include device and app details.',
			'Tapping the active navigation item closes open pages or scrolls back to the top.',
			'Various bug fixes and style improvements.',
		],
	},
	{
		id: '2.0-collection-shuffle-friends',
		version: '2.0',
		date: 'September 23, 2026',
		title: 'Collection, shuffle, and friends',
		summary:
			'A major update to Tunetoss brings friends and activity, personal ratings and favorites, and a refreshed shuffle and collection experience.',
		items: [
			'Added friends and a recent activity feed for listens, likes, ratings, and collection additions.',
			'Redesigned shuffle with a cover carousel and saved default filters.',
			'Redesigned collection and shuffle filters.',
			'Added personal release ratings, including collection filters and sorting.',
			'Added release favorites with dedicated collection filtering and cover indicators.',
			'Smoother page navigation keeps your filters, loaded results, and scroll position when switching pages.',
			'Various bug fixes, style and UX improvements.',
		],
	},
	{
		id: '1.7-smoother-collection-loading',
		version: '1.7',
		date: 'July 18, 2026',
		title: 'Smoother collection loading',
		summary:
			'Tunetoss now keeps active collections ready in the background.',
		items: [
			'Active collections are refreshed in the background for faster return visits.',
			'Various bug fixes and improvements.',
		],
	},
	{
		id: '1.6-release-notes',
		version: '1.6',
		date: 'July 13, 2026',
		title: 'Release notes',
		summary:
			'Tunetoss now has release notes in-app, with a small prompt for new releases and a full history in Settings.',
		items: [
			'Added a release dialog for new app versions.',
			'Added a release notes entry in Settings under Help & About.',
			'Added Later and Dismiss behavior so release prompts can be deferred or marked as seen.',
			'Added a complete release history from launch through recent 1.x updates.',
		],
	},
	{
		id: '1.5-history-transfer-recommendations',
		version: '1.5',
		date: 'July 7, 2026',
		title: 'History tools and recommendations',
		summary:
			'Tunetoss gained stronger history transfer tools and more ways to continue exploring from a release.',
		items: [
			'Added CSV import and export for listening history.',
			'Added similar release recommendations, including same-label and same-artist sections.',
			'Added a setting to show or hide collection value.',
			'Refined multiselect persistence and carousel scrolling.',
		],
	},
	{
		id: '1.4-collection-navigation-filters',
		version: '1.4',
		date: 'July 4, 2026',
		title: 'Collection navigation and filters',
		summary:
			'Collection browsing became faster to scan, search, and tune to personal defaults.',
		items: [
			'Added collection search.',
			'Added grouped collection navigation.',
			'Added multiselect filters.',
			'Added default collection filter settings.',
			'Added collection value display.',
		],
	},
	{
		id: '1.3-preview-playback',
		version: '1.3',
		date: 'June 28, 2026',
		title: 'Preview playback',
		summary:
			'Tunetoss added in-app track previews and tightened collection loading around sync state.',
		items: [
			'Added in-app track preview playback.',
			'Added Discogs ID controls to release details.',
			'Gated collection routes until sync completes.',
			'Moved private collection fallback messaging into collection readiness.',
			'Improved shuffle and hide-duration filter behavior.',
		],
	},
	{
		id: '1.2-settings-collection-reliability',
		version: '1.2',
		date: 'June 25, 2026',
		title: 'Settings and collection reliability',
		summary:
			'Settings, collection data freshness, and release-history editing got more robust.',
		items: [
			'Moved app settings into an overlay.',
			'Added typed confirmations for destructive actions.',
			'Improved release-history date editing and duplicate listen handling.',
			'Added master year to collection basics.',
			'Added Discogs attribution and stale API data expiration.',
		],
	},
	{
		id: '1.1-help-support',
		version: '1.1',
		date: 'June 19, 2026',
		title: 'Help and support',
		summary:
			'The first post-launch update added Help & About content and support workflows.',
		items: [
			'Added Help & About settings overlays.',
			'Added contact and bug report forms.',
			'Connected support submissions to email delivery.',
		],
	},
	{
		id: '1.0-launch',
		version: '1.0',
		date: 'June 19, 2026',
		title: 'Launch',
		summary:
			'Tunetoss is live: a small app for rediscovering a Discogs collection one shuffle at a time.',
		items: [
			'The app went live on June 19, 2026.',
			'Shuffle, collection browsing, settings, and listening-history basics are ready for regular use.',
			'Thanks for being part of the first release.',
		],
	},
]

export function getCurrentReleaseNote(): ReleaseNote | null {
	return RELEASE_NOTES[0] ?? null
}
